<template>
  <div class="payee-summary-modal">
    <!-- Title Bar with Refresh and Close Buttons -->
    <div class="title-bar">
      <div class="title-content">
        <h3>Payee Summary</h3>
      </div>
      <div class="title-buttons">
        <button
          v-if="selectedPayee"
          class="link-btn"
          :class="{ 'link-btn-active': selectedPayeeHasAlias }"
          @click="handleLinkClick"
          :title="
            selectedPayeeHasAlias
              ? 'Click to unlink alias'
              : 'Link as alias to another payee'
          "
        >
          <i class="fas fa-link"></i>
        </button>
        <button
          v-if="selectedPayee"
          class="edit-btn"
          @click="openEditAddress"
          title="Edit payee details"
        >
          <i class="fas fa-edit"></i>
        </button>
        <button
          class="collapse-btn"
          :class="{ 'collapse-btn-active': aliasesCollapsed }"
          @click="aliasesCollapsed = !aliasesCollapsed"
          :title="aliasesCollapsed ? 'Show all payees' : 'Collapse aliases'"
        >
          <i class="fas fa-compress-alt"></i>
        </button>
        <button
          class="excel-btn"
          @click="exportToExcel"
          title="Export to Excel"
        >
          <i class="fas fa-file-excel"></i>
        </button>
        <button
          class="print-btn"
          @click="printPayeeSummary"
          title="Print Payee Summary"
        >
          <i class="fas fa-print"></i>
        </button>
        <button class="refresh-btn" @click="refreshData" title="Refresh Data">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="closePayeeSummary">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Loading / Error States -->
    <div v-if="loading" class="loading-indicator">
      <i class="fas fa-spinner fa-spin"></i> Loading payee data...
    </div>
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Filter Input -->
    <div v-if="!loading && !error" class="filter-container">
      <input
        type="text"
        v-model="filterText"
        placeholder="Filter by Display Name or Name..."
        class="filter-input"
        ref="filterInput"
        @focus="selectFilterText"
        @keydown.esc="filterText = ''"
      />
      <select
        v-model="selectedYear"
        class="year-select"
        @change="loadPayeeData"
      >
        <option v-for="year in availableYears" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
      <span class="filter-help"
        >Comma chains filters, ! negates (e.g., "vendor,!other" or "!consultant"
        or "!" to find blank types)</span
      >
      <span v-if="filterText" class="filter-count">
        {{ filteredPayees.length }} of {{ payees.length }}
      </span>
    </div>

    <!-- Payee Table -->
    <div
      v-if="!loading && !error"
      class="table-container"
      @mouseenter="mouseOverTable = true"
      @mouseleave="mouseOverTable = false"
    >
      <table id="printablePayeeTable" class="payee-table">
        <thead>
          <tr class="header-row">
            <th class="name-col">Display Name</th>
            <th class="amount-col sortable" @click="toggleSort('total2024')">
              {{ selectedYear - 1 }} Total
              <span v-if="sortColumn === 'total2024'" class="sort-indicator">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th class="amount-col sortable" @click="toggleSort('total2025')">
              {{ selectedYear }} Total
              <span v-if="sortColumn === 'total2025'" class="sort-indicator">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th class="info-col">Name</th>
            <th class="info-col">Business</th>
            <th class="address-col">Address</th>
            <th class="location-col">City/State/Zip</th>
            <th class="type-col">Type</th>
            <th class="flag-col">1099</th>
            <th class="taxid-col">Tax ID</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(payee, index) in filteredPayees"
            :key="payee.displayName"
            :class="{
              'zebra-stripe':
                index % 2 === 1 && selectedPayee !== payee.displayName,
              'row-selected': selectedPayee === payee.displayName,
            }"
            @click="toggleRowSelection(payee)"
          >
            <td
              class="name-cell clickable"
              :class="{
                'linking-mode':
                  linkingMode && selectedPayee !== payee.displayName,
                'is-alias': payee.aliasOf,
                'has-collapsed-aliases':
                  payee.hasCollapsedAliases || payee.isOfficialWithAliases,
              }"
              @dblclick="handleDisplayNameDblClick(payee)"
              :title="getDisplayNameTooltip(payee)"
              v-html="highlightText(payee.displayName)"
            ></td>
            <td class="amount-cell">{{ formatCurrency(payee.total2024) }}</td>
            <td class="amount-cell">{{ formatCurrency(payee.total2025) }}</td>
            <td class="info-cell" v-html="highlightText(payee.fullName)"></td>
            <td class="info-cell">{{ payee.businessName }}</td>
            <td class="address-cell">{{ payee.address }}</td>
            <td class="location-cell">{{ payee.cityStateZip }}</td>
            <!-- prettier-ignore -->
            <td
              class="type-cell"
              :class="{ 'type-empty': !payee.payeeType && editingPayee !== payee.displayName }"
              @dblclick="startEditType(payee)"
              :title="
                editingPayee === payee.displayName
                  ? ''
                  : 'Double-click to edit'
              "
            >
              <select
                v-if="editingPayee === payee.displayName"
                v-model="payee.payeeType"
                @change="savePayeeType(payee)"
                @blur="cancelEditType"
                class="type-select"
                ref="typeSelect"
              >
                <option :value="1">Vendor</option>
                <option :value="8">Contractor</option>
                <option :value="6">Consultant</option>
                <option :value="7">Employee</option>
                <option :value="2">Tenant</option>
                <option :value="3">Customer</option>
                <option :value="5">Other</option>
              </select>
              <span v-else-if="getPayeeTypeName(payee.payeeType)">{{ getPayeeTypeName(payee.payeeType) }}</span>
              <span v-else class="type-placeholder">···</span>
            </td>
            <td class="flag-cell">
              <span v-if="payee.is1099" class="is1099-yes">✓</span>
              <span v-else class="is1099-no">-</span>
            </td>
            <td
              class="taxid-cell"
              :class="{ 'taxid-missing': payee.is1099 && !payee.taxId }"
              @click="toggleTaxId(payee)"
              :title="payee.showTaxId ? 'Click to hide' : 'Click to reveal'"
            >
              {{ payee.showTaxId ? payee.taxId : maskTaxId(payee.taxId) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ManageAddress Modal -->
    <ManageAddress
      :showModal="showAddressModal"
      :name="selectedPayee"
      :id="null"
    />
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import ManageAddress from './ManageAddress.vue';
import eventBus from './eventBus';

export default {
  name: 'PayeeSummary',
  components: {
    ManageAddress,
  },
  props: {
    userRole: {
      type: Number,
      default: null,
    },
    initialFilter: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      payees: [],
      loading: false,
      error: null,
      editingPayee: null,
      filterText: '',
      selectedPayee: null,
      selectedYear:
        new Date().getMonth() < 2
          ? new Date().getFullYear() - 1
          : new Date().getFullYear(),
      linkingMode: false,
      linkingSourcePayee: null,
      aliasesCollapsed: true,
      showAddressModal: false,
      mouseOverTable: false,
      sortColumn: null, // 'total2024' or 'total2025'
      sortDirection: null, // 'asc', 'desc', or null (default alpha)
    };
  },
  computed: {
    hasAccess() {
      return this.userRole < 2;
    },
    selectedPayeeHasAlias() {
      if (!this.selectedPayee) return false;
      const payee = this.payees.find(
        (p) => p.displayName === this.selectedPayee,
      );
      return payee && payee.aliasOf;
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 2024; y--) {
        years.push(y);
      }
      return years;
    },
    filteredPayees() {
      let result = this.payees;

      // Build a set of official payees (those that have aliases pointing to them)
      const officialPayees = new Set();
      result.forEach((payee) => {
        if (payee.aliasOf) {
          officialPayees.add(payee.aliasOf);
        }
      });

      // If aliases are collapsed, aggregate alias totals into parent rows
      if (this.aliasesCollapsed) {
        // Build a map of official payee -> sum of alias totals
        const aliasTotals = {};
        result.forEach((payee) => {
          if (payee.aliasOf) {
            if (!aliasTotals[payee.aliasOf]) {
              aliasTotals[payee.aliasOf] = { total2024: 0, total2025: 0 };
            }
            aliasTotals[payee.aliasOf].total2024 += payee.total2024 || 0;
            aliasTotals[payee.aliasOf].total2025 += payee.total2025 || 0;
          }
        });

        // Filter out aliases and add alias totals to parent rows
        result = result
          .filter((payee) => !payee.aliasOf)
          .map((payee) => {
            if (aliasTotals[payee.displayName]) {
              return {
                ...payee,
                total2024:
                  (payee.total2024 || 0) +
                  aliasTotals[payee.displayName].total2024,
                total2025:
                  (payee.total2025 || 0) +
                  aliasTotals[payee.displayName].total2025,
                hasCollapsedAliases: true,
              };
            }
            return payee;
          });
      } else {
        // Expanded view: sort aliases below their official parent row
        // Mark official rows (those that have aliases) for bold styling
        result = result.map((payee) => ({
          ...payee,
          isOfficialWithAliases: officialPayees.has(payee.displayName),
        }));

        // Sort: group aliases under their parent
        result = result.sort((a, b) => {
          // Get the "sort key" - for aliases, use their aliasOf; otherwise use displayName
          const aKey = a.aliasOf || a.displayName;
          const bKey = b.aliasOf || b.displayName;

          // First, sort by the group key
          if (aKey !== bKey) {
            return aKey.localeCompare(bKey);
          }

          // Within the same group, official row comes first (no aliasOf), then aliases
          if (a.aliasOf && !b.aliasOf) return 1;
          if (!a.aliasOf && b.aliasOf) return -1;

          // Both are aliases of the same parent, sort alphabetically
          return a.displayName.localeCompare(b.displayName);
        });
      }

      // Apply filter if there's filter text
      if (this.filterText.trim()) {
        // Special case: "!" by itself shows rows with empty Type
        if (this.filterText.trim() === '!') {
          result = result.filter((payee) => !payee.payeeType);
        } else if (this.filterText.trim().toLowerCase() === '1099') {
          // Special case: "1099" shows rows where 1099 flag is true
          result = result.filter((payee) => payee.is1099);
        } else {
          const tokens = this.filterText
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t);
          if (tokens.length > 0) {
            for (const token of tokens) {
              const isNegated = token.startsWith('!');
              const term = (
                isNegated ? token.substring(1).trim() : token
              ).toLowerCase();
              if (!term) continue;
              result = result.filter((payee) => {
                const displayName = (payee.displayName || '').toLowerCase();
                const fullName = (payee.fullName || '').toLowerCase();
                // prettier-ignore
                const typeName = this.getPayeeTypeName(payee.payeeType).toLowerCase();
                const aliasOf = (payee.aliasOf || '').toLowerCase();
                const matches =
                  displayName.includes(term) ||
                  fullName.includes(term) ||
                  typeName.includes(term) ||
                  aliasOf.includes(term);
                return isNegated ? !matches : matches;
              });
            }
          }
        }
      }

      // Apply numeric sorting if a sort column is selected
      if (this.sortColumn && this.sortDirection) {
        const col = this.sortColumn;
        const dir = this.sortDirection === 'asc' ? 1 : -1;
        result = [...result].sort((a, b) => {
          const aVal = a[col] || 0;
          const bVal = b[col] || 0;
          return (aVal - bVal) * dir;
        });
      }

      return result;
    },
  },
  mounted() {
    if (this.userRole >= 2) {
      console.warn('PayeeSummary: Access denied - insufficient permissions');
      this.$emit('close');
      return;
    }
    // Set initial filter from prop if provided
    if (this.initialFilter) {
      this.filterText = this.initialFilter;
    }
    this.loadPayeeData();
    document.addEventListener('keydown', this.handleKeydown);
    eventBus.on('closeAddressModal', this.handleAddressModalClosed);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    eventBus.off('closeAddressModal', this.handleAddressModalClosed);
  },
  methods: {
    openEditAddress() {
      if (this.selectedPayee) {
        this.showAddressModal = true;
      }
    },

    handleAddressModalClosed() {
      this.showAddressModal = false;
      this.refreshData();
    },

    async loadPayeeData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          `/api/payee-summary?year=${this.selectedYear}`,
        );
        if (response.data && Array.isArray(response.data)) {
          // Add showTaxId property to each payee for toggle functionality
          this.payees = response.data.map((p) => ({
            ...p,
            showTaxId: false,
          }));
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error loading payee data:', error);
        this.error = 'Failed to load payee data. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.loadPayeeData();
    },

    closePayeeSummary() {
      this.$emit('close');
    },

    formatCurrency(value) {
      if (!value || value === 0) return '';
      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return '';
      const absNum = Math.abs(num);
      const formatted = absNum.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return num < 0 ? `(${formatted})` : formatted;
    },

    getPayeeTypeName(type) {
      const types = {
        1: 'Vendor',
        2: 'Tenant',
        3: 'Customer',
        4: 'Other',
        5: 'Other',
        6: 'Consultant',
        7: 'Employee',
        8: 'Contractor',
      };
      return types[type] || '';
    },

    maskTaxId(taxId) {
      if (!taxId) return '';
      return '****';
    },

    toggleTaxId(vendor) {
      vendor.showTaxId = !vendor.showTaxId;
    },

    handleDisplayNameDblClick(payee) {
      // If in collapsed mode and this row has collapsed aliases, expand and filter
      if (this.aliasesCollapsed && payee.hasCollapsedAliases) {
        this.aliasesCollapsed = false;
        // Filter to show the official payee and all its aliases
        this.filterText = payee.displayName;
        return;
      }
      // Otherwise, open in ledger
      this.openInLedger(payee.displayName);
    },

    openInLedger(payeeName) {
      // Build URL for MainPage with search parameter and YTD date range
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
      const currentDay = String(today.getDate()).padStart(2, '0');
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-${currentMonth}-${currentDay}`;

      const url = `/main?page=bank&q=${encodeURIComponent(payeeName)}&start=${startDate}&end=${endDate}`;

      // Open in new tab
      window.open(url, '_blank');
    },

    startEditType(payee) {
      this.editingPayee = payee.displayName;
      this.$nextTick(() => {
        const select = this.$refs.typeSelect;
        if (select) {
          // Handle array of refs (v-for creates array)
          const selectEl = Array.isArray(select) ? select[0] : select;
          if (selectEl) {
            selectEl.focus();
          }
        }
      });
    },

    cancelEditType() {
      this.editingPayee = null;
    },

    async savePayeeType(payee) {
      try {
        await axios.put('/api/payee-summary/payee-type', {
          displayName: payee.displayName,
          payeeType: payee.payeeType,
        });
        this.editingPayee = null;
      } catch (error) {
        console.error('Error saving payee type:', error);
        alert('Failed to save payee type');
      }
    },

    getDisplayNameTooltip(payee) {
      let tooltip = 'Double-click Display Name to search in ledger';
      if (payee.hasCollapsedAliases) {
        tooltip = 'Double-click to show aliases';
      }
      if (payee.aliasOf) {
        tooltip += `\n(alias of "${payee.aliasOf}")`;
      }
      if (this.linkingMode && payee.displayName !== this.linkingSourcePayee) {
        tooltip = `Click to link "${this.linkingSourcePayee}" as alias of this payee`;
      }
      return tooltip;
    },

    handleLinkClick() {
      if (this.selectedPayeeHasAlias) {
        // Unlink the alias
        this.unlinkAlias();
      } else {
        // Start linking mode
        this.startLinkingMode();
      }
    },

    startLinkingMode() {
      this.linkingSourcePayee = this.selectedPayee;
      this.selectedPayee = null;
      this.linkingMode = true;
    },

    cancelLinkingMode() {
      this.linkingMode = false;
      this.linkingSourcePayee = null;
    },

    async completeAliasLink(targetDisplayName) {
      try {
        await axios.put('/api/payee-summary/alias', {
          displayName: this.linkingSourcePayee,
          aliasOf: targetDisplayName,
        });
        // Update local data
        const payee = this.payees.find(
          (p) => p.displayName === this.linkingSourcePayee,
        );
        if (payee) {
          payee.aliasOf = targetDisplayName;
        }
        this.cancelLinkingMode();
      } catch (error) {
        console.error('Error linking alias:', error);
        alert('Failed to link alias');
        this.cancelLinkingMode();
      }
    },

    async unlinkAlias() {
      try {
        const payee = this.payees.find(
          (p) => p.displayName === this.selectedPayee,
        );
        if (!payee) return;

        await axios.put('/api/payee-summary/alias', {
          displayName: this.selectedPayee,
          aliasOf: null,
        });
        // Update local data
        payee.aliasOf = null;
        this.selectedPayee = null;
      } catch (error) {
        console.error('Error unlinking alias:', error);
        alert('Failed to unlink alias');
      }
    },

    highlightText(text) {
      if (!text || !this.filterText.trim()) {
        return text || '';
      }
      const term = this.filterText.trim();
      const regex = new RegExp(
        `(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi',
      );
      return text.replace(regex, '<mark class="highlight">$1</mark>');
    },

    selectFilterText(event) {
      if (this.filterText) {
        event.target.select();
      }
    },

    toggleRowSelection(payee) {
      // If in linking mode, this is the second selection
      if (this.linkingMode) {
        if (payee.displayName !== this.linkingSourcePayee) {
          this.completeAliasLink(payee.displayName);
        }
        return;
      }
      // Normal selection toggle
      if (this.selectedPayee === payee.displayName) {
        this.selectedPayee = null;
      } else {
        this.selectedPayee = payee.displayName;
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.linkingMode) {
          this.cancelLinkingMode();
        } else if (this.mouseOverTable && this.filterText) {
          this.filterText = '';
        } else if (this.selectedPayee) {
          this.selectedPayee = null;
        } else if (!this.filterText && this.$refs.filterInput) {
          this.$refs.filterInput.focus();
        }
      }
    },

    toggleSort(column) {
      if (this.sortColumn !== column) {
        // Clicking a different column: start with ascending
        this.sortColumn = column;
        this.sortDirection = 'asc';
      } else if (this.sortDirection === 'asc') {
        // Same column, was ascending: switch to descending
        this.sortDirection = 'desc';
      } else {
        // Same column, was descending: reset to default (alpha)
        this.sortColumn = null;
        this.sortDirection = null;
      }
    },

    printPayeeSummary() {
      const table = document.getElementById('printablePayeeTable');
      const printContent = table.outerHTML;
      const currYear = this.selectedYear;

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Payee Summary - ${currYear}</title>
            <style>
              @page {
                size: landscape;
                margin: 0.5in 0.25in;
              }

              body {
                font-family: Calibri, Arial, sans-serif;
                margin: 0;
                padding: 10px;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.7em;
              }

              table, th, td {
                border: 1px solid #ddd;
              }

              th, td {
                padding: 4px 6px;
                text-align: left;
              }

              th {
                background-color: #f8f9fa !important;
                font-weight: 600;
              }

              /* Header alignment: amount columns right-aligned, type centered */
              th.amount-col {
                text-align: right;
              }

              th.type-col {
                text-align: center;
              }

              tbody tr:nth-child(even) {
                background-color: #ededed !important;
              }

              tbody tr:nth-child(odd) {
                background-color: #ffffff !important;
              }

              .amount-cell {
                text-align: right;
                font-family: 'Courier New', monospace;
              }

              .type-cell, .flag-cell, .taxid-cell {
                text-align: center;
              }

              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <h2 style="text-align: center; margin-bottom: 10px;">Payee Summary - ${currYear}</h2>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    async exportToExcel() {
      try {
        // Check if there are payees to export
        if (!this.filteredPayees || this.filteredPayees.length === 0) {
          alert('No payees to export. Please adjust your filters.');
          return;
        }

        // Prepare payees data with type name resolved
        const payeesWithTypeName = this.filteredPayees.map((payee) => ({
          ...payee,
          payeeTypeName: this.getPayeeTypeName(payee.payeeType) || '',
        }));

        const response = await axios.post(
          '/api/export-payee-summary',
          { payees: payeesWithTypeName, year: this.selectedYear },
          { responseType: 'blob' },
        );

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = `payee_summary_${this.selectedYear}.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2)
            filename = filenameMatch[1];
        }

        // Create a URL for the file and trigger a download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Failed to export to Excel. Please try again.');
      }
    },
  },
};
</script>

<style scoped>
.payee-summary-modal {
  position: relative;
  width: 90vw;
  max-width: 1400px;
  height: 95vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #a080a8;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  margin-bottom: 0;
  flex-shrink: 0;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
}

.filter-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.filter-input {
  flex: 1;
  max-width: 300px;
  padding: 6px 10px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  outline: none;
}

.year-select {
  padding: 6px 10px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  outline: none;
  background: white;
  cursor: pointer;
}

.year-select:focus {
  border-color: #a080a8;
  box-shadow: 0 0 3px rgba(160, 128, 168, 0.3);
}

.filter-input:focus {
  border-color: #a080a8;
  box-shadow: 0 0 3px rgba(160, 128, 168, 0.3);
}

.filter-help {
  font-size: 0.75em;
  color: #999;
  font-style: italic;
}

.filter-count {
  font-size: 0.85em;
  color: #666;
}

:deep(mark.highlight) {
  background-color: #d4c4d9 !important;
  padding: 0 2px;
  border-radius: 2px;
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn,
.close-btn,
.print-btn,
.excel-btn,
.link-btn,
.collapse-btn,
.edit-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.refresh-btn {
  margin-right: 30px;
}

.refresh-btn:hover,
.close-btn:hover,
.print-btn:hover,
.excel-btn:hover,
.link-btn:hover,
.collapse-btn:hover,
.edit-btn:hover {
  background: #a080a8;
  color: #fff;
  border-color: #fff;
}

.link-btn-active,
.collapse-btn-active {
  background: #5a4a5c;
  border-color: #fff;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
}

.link-btn-active:hover,
.collapse-btn-active:hover {
  background: #6a5a6c;
}

.excel-btn {
  color: #d4edda;
}

.refresh-btn i,
.close-btn i {
  font-size: 12px;
}

.table-container {
  flex: 1;
  overflow: auto;
  padding: 0;
  margin: 0 10px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-container::-webkit-scrollbar {
  display: none;
}

.payee-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75em;
  font-family: Calibri, sans-serif;
  background: white;
}

.header-row {
  background: #a080a8;
  border-top: 2px solid #ccc;
  border-bottom: 2px solid #ccc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-row th {
  padding: 6px 8px;
  text-align: left;
  font-weight: 600;
  color: #fff;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  font-family: Calibri, sans-serif;
  white-space: nowrap;
}

.header-row th.sortable {
  cursor: pointer;
  user-select: none;
}

.header-row th.sortable:hover {
  background: #8a6a92;
}

.sort-indicator {
  margin-left: 4px;
  font-size: 10px;
}

.name-col {
  min-width: 150px;
}

.amount-col {
  min-width: 80px;
  text-align: right !important;
}

.info-col {
  min-width: 120px;
}

.address-col {
  min-width: 150px;
}

.location-col {
  min-width: 140px;
}

.type-col {
  min-width: 70px;
  text-align: center !important;
}

.flag-col {
  min-width: 40px;
  text-align: center !important;
}

.taxid-col {
  min-width: 60px;
  text-align: center !important;
}

tbody tr {
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

tbody tr:hover {
  background-color: #f5f0f6;
}

.row-selected {
  background-color: #c9b3cf !important;
}

.zebra-stripe {
  background-color: #ededed;
}

td {
  padding: 6px 8px;
  text-align: left;
  border-right: 1px solid #f5f5f5;
  vertical-align: middle;
  font-family: Calibri, sans-serif;
  height: 28px;
  box-sizing: border-box;
}

.name-cell {
  font-weight: 500;
}

.name-cell.clickable {
  cursor: pointer;
}

.name-cell.clickable:hover {
  text-decoration: underline;
  color: #7b2cbf;
}

.name-cell.linking-mode {
  cursor: pointer;
  background-color: #e8f5e9 !important;
}

.name-cell.linking-mode:hover {
  background-color: #c8e6c9 !important;
  text-decoration: underline;
  color: #2e7d32;
}

.name-cell.is-alias {
  font-style: italic;
}

.name-cell.has-collapsed-aliases {
  font-weight: bold;
}

.amount-cell {
  text-align: right;
  font-family: 'Courier New', monospace;
}

.info-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-cell {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.location-cell {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-cell {
  text-align: center;
  cursor: pointer;
  width: 85px;
  min-width: 85px;
  max-width: 85px;
}

.type-cell:hover {
  background-color: #f3e5f5;
}

.type-cell.type-empty {
  border: 1px dashed #ccc;
}

.type-cell.type-empty:hover {
  background-color: #f3e5f5;
  border-color: #a080a8;
}

.type-placeholder {
  color: #bbb;
  font-size: 1.2em;
  letter-spacing: 2px;
}

.type-select {
  width: 100%;
  padding: 0 4px;
  font-size: inherit;
  font-family: inherit;
  border: none;
  border-radius: 3px;
  background: #f3e5f5;
  cursor: pointer;
  height: 100%;
  box-sizing: border-box;
}

.type-select:focus {
  outline: 2px solid #a080a8;
  outline-offset: -2px;
}

.flag-cell {
  text-align: center;
}

.is1099-yes {
  color: #28a745;
  font-weight: bold;
}

.is1099-no {
  color: #999;
}

.taxid-cell {
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.taxid-cell:hover {
  background-color: #f3e5f5;
}

.taxid-missing {
  border: 2px solid #e53935;
  background-color: #ffebee;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1em;
}

.loading-indicator i {
  margin-right: 8px;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 15px 20px;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  margin: 20px;
  text-align: center;
}

@media (max-width: 1200px) {
  .payee-summary-modal {
    width: 95vw;
  }

  .payee-table {
    font-size: 0.7em;
  }
}
</style>
